import { useState, useRef, useEffect } from "react";
import { createPortal } from "react-dom";
import { Reveal } from "@/components/site/Reveal";
import { ChevronLeft, ChevronRight, Maximize2, X } from "lucide-react";

/* ─────────────────────────────────────────────────────────────────────────
   ⚙️  ΒΙΝΤΕΟ ΑΝΑ ΠΡΟΪΟΝ
   Πρόσθεσε / αφαίρεσε paths εδώ. Τα αρχεία πρέπει να υπάρχουν στο public/.
   ───────────────────────────────────────────────────────────────────────── */
const VIDEOS = {
  mailer: ["/mailer-1.mp4", "/mailer-2.mp4"],
  box: ["/box-1.mp4", "/box-2.mp4"],
  book: ["/book-1.mp4", "/book-2.mp4"],
  custombox: ["/custombox-1.mp4"],
};

/* ── Video Carousel ─────────────────────────────────────────────────────── */
const VideoCarousel = ({ videos, className = "" }) => {
  const [active, setActive] = useState(0);
  const [expanded, setExpanded] = useState(false);
  const videoRef = useRef(null);

  const count = videos.length;
  const prev = () => setActive((a) => (a - 1 + count) % count);
  const next = () => setActive((a) => (a + 1) % count);

  // Auto-play current video when slide changes
  useEffect(() => {
    const v = videoRef.current;
    if (!v) return;
    v.load();
    v.play().catch(() => {});
  }, [active]);

  // Close expanded view with Escape
  useEffect(() => {
    if (!expanded) return;
    const onKey = (e) => e.key === "Escape" && setExpanded(false);
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [expanded]);

  return (
    <div className="relative">
      <video
        ref={videoRef}
        className={className}
        src={videos[active]}
        muted
        loop
        playsInline
        preload="metadata"
      />

      {/* Expand button */}
      <button
        type="button"
        onClick={() => setExpanded(true)}
        aria-label="Μεγέθυνση βίντεο"
        className="absolute right-2 top-2 z-10 flex h-7 w-7 items-center justify-center rounded-full bg-black/50 text-white/90 backdrop-blur-sm transition-colors hover:bg-black/70"
      >
        <Maximize2 className="h-3.5 w-3.5" />
      </button>

      {/* Arrows */}
      {count > 1 && (
        <>
          <button
            type="button"
            onClick={prev}
            aria-label="Προηγούμενο"
            className="absolute left-2 top-1/2 z-10 flex h-7 w-7 -translate-y-1/2 items-center justify-center rounded-full bg-black/50 text-white/90 backdrop-blur-sm transition-colors hover:bg-black/70"
          >
            <ChevronLeft className="h-4 w-4" />
          </button>
          <button
            type="button"
            onClick={next}
            aria-label="Επόμενο"
            className="absolute right-2 top-1/2 z-10 flex h-7 w-7 -translate-y-1/2 items-center justify-center rounded-full bg-black/50 text-white/90 backdrop-blur-sm transition-colors hover:bg-black/70"
          >
            <ChevronRight className="h-4 w-4" />
          </button>
        </>
      )}

      {/* Dots */}
      {count > 1 && (
        <div className="absolute bottom-2 left-1/2 z-10 flex -translate-x-1/2 gap-1.5">
          {videos.map((_, i) => (
            <button
              key={i}
              type="button"
              onClick={() => setActive(i)}
              aria-label={`Slide ${i + 1}`}
              className={`h-1.5 rounded-full transition-all ${
                i === active
                  ? "w-4 bg-white"
                  : "w-1.5 bg-white/40 hover:bg-white/70"
              }`}
            />
          ))}
        </div>
      )}

      {/* Expanded view — enlarged & centered, page still visible behind.
          Rendered in a portal so it escapes the mockup's CSS transform. */}
      {expanded &&
        createPortal(
          <div
            className="fixed inset-0 z-[120] flex items-center justify-center bg-black/70 p-4 backdrop-blur-sm"
            onClick={() => setExpanded(false)}
          >
            <div
              className="relative w-full max-w-[1536px]"
              onClick={(e) => e.stopPropagation()}
            >
              <button
                type="button"
                onClick={() => setExpanded(false)}
                aria-label="Κλείσιμο"
                className="absolute -top-11 right-0 flex h-9 w-9 items-center justify-center rounded-full bg-white/15 text-white transition-colors hover:bg-white/25"
              >
                <X className="h-5 w-5" />
              </button>
              <video
                className="max-h-[90vh] w-full rounded-xl bg-black object-contain shadow-2xl"
                src={videos[active]}
                controls
                autoPlay
                loop
                muted
                playsInline
              />
            </div>
          </div>,
          document.body
        )}
    </div>
  );
};

/* ── Device Mockup ──────────────────────────────────────────────────────────
   One unified frame (the Video Mailer template) for all four products:
   top notch, video, caption bottom-left + red dot bottom-right.
   Only the video and the caption label change per product.
   ─────────────────────────────────────────────────────────────────────────── */
const DeviceMockup = ({ type, label }) => {
  const videos = VIDEOS[type] || ["/hero-scrub.mp4"];

  return (
    <div className="relative mx-auto w-full max-w-[760px] animate-float-y">
      <div className="rounded-[26px] bg-gradient-to-b from-white/12 to-white/[0.04] p-5 shadow-2xl ring-1 ring-white/10">
        <div className="mx-auto mb-5 h-2 w-24 rounded-full bg-white/20" />
        <div className="relative aspect-[16/9] overflow-hidden rounded-[16px]">
          <VideoCarousel videos={videos} className="h-full w-full object-cover" />
          <div className="reflect pointer-events-none absolute inset-0" />
        </div>
        <div className="mt-5 flex items-center justify-between px-1">
          <span className="text-xs uppercase tracking-[0.3em] text-white/40">
            {label}
          </span>
          <span className="h-3 w-3 rounded-full bg-brand-400" />
        </div>
      </div>
    </div>
  );
};

/* ── Product Data ───────────────────────────────────────────────────────── */
const PRODUCTS = [
  {
    id: "01",
    type: "mailer",
    name: "Video Brochure",
    desc: "Στείλε το μήνυμά σου με στυλ και άφησε το κοινό να σε θυμάται. Ο παραλήπτης δεν λαμβάνει απλώς έναν φάκελο. Παραλαμβάνει μια εμπειρία που ανοίγει και παίζει αυτόματα το βίντεό σου.",
    use: "Ιδανικό για στοχευμένες καμπάνιες, VIP επικοινωνία ή παρουσιάσεις που θέλεις να μείνουν αξέχαστες.",
  },
  {
    id: "02",
    type: "box",
    name: "Video Boxes",
    desc: "Κάνε το unboxing μια εμπειρία. Τα Video Boxes συνδυάζουν premium συσκευασία με ενσωματωμένη οθόνη βίντεο, που παίζει αυτόματα το μήνυμά σου μόλις ανοίξει το κουτί.",
    use: "Ιδανικά για δειγματισμούς, VIP kits και εταιρικά δώρα που θέλεις να ξεχωρίσουν.",
  },
  {
    id: "03",
    type: "book",
    name: "Premium Book",
    desc: "Η πιο εκλεπτυσμένη εκδοχή του video brochure. Σκληρό εξώφυλλο, προστατευτική θήκη και υλικά υψηλής αισθητικής δίνουν στο μήνυμά σου το κύρος ενός βιβλίου συλλογής. Μόλις ανοίξει, η οθόνη ζωντανεύει και αφηγείται την ιστορία του brand σου.",
    use: "Ιδανικό για παρουσιάσεις υψηλού επιπέδου, luxury προϊόντα και πελάτες που θέλεις να εντυπωσιάσεις από το πρώτο άγγιγμα.",
  },
  {
    id: "04",
    type: "custombox",
    name: "Custom Box",
    desc: "Μια συσκευασία φτιαγμένη αποκλειστικά για σένα, που φιλοξενεί το δώρο και το βίντεό σου στον ίδιο χώρο. Τοποθέτησε ένα κρασί, ένα δείγμα ή ένα εταιρικό δώρο δίπλα στην οθόνη και χάρισε μια εμπειρία που ανοίγει σαν θησαυρός.",
    use: "Τέλειο για εταιρικά δώρα, premium gifting και αφορμές που αξίζουν κάτι παραπάνω από ένα απλό κουτί.",
  },
];

/* ── Section ────────────────────────────────────────────────────────────── */
export const Products = () => {
  return (
    <section
      id="products"
      data-testid="products-section"
      className="relative scroll-mt-24 overflow-hidden bg-ink py-24 text-white sm:py-36"
    >
      <div className="grain" />
      <div className="relative mx-auto max-w-[1920px] px-5 sm:px-8 lg:px-16">
        <Reveal>
          <span className="text-sm font-semibold uppercase tracking-[0.3em] text-brand-300">
            · ΤΑ ΠΡΟΙΟΝΤΑ ΜΑΣ
          </span>
          <h2 className="mt-8 max-w-4xl font-display text-5xl font-bold leading-[1.04] sm:text-7xl">
            Τέσσερις τρόποι να κάνεις το brand σου{" "}
            <span className="text-brand-400">αξέχαστο.</span>
          </h2>
        </Reveal>

        <div className="mt-20 flex flex-col gap-24 sm:mt-28 sm:gap-52">
          {PRODUCTS.map((p, i) => {
            const flip = i % 2 === 1;
            return (
              <div
                key={p.id}
                data-testid={`product-${p.id}`}
                className="grid items-center gap-12 lg:grid-cols-2 lg:gap-24"
              >
                <Reveal className={flip ? "lg:order-2" : ""}>
                  <div>
                    <div className="flex items-baseline gap-5">
                      <span className="font-display text-7xl font-extrabold text-stroke-red sm:text-8xl">
                        {p.id}
                      </span>
                      <span className="h-px flex-1 bg-white/15" />
                    </div>
                    <h3 className="mt-8 font-display text-4xl font-bold sm:text-5xl">
                      {p.name}
                    </h3>
                    <p className="mt-7 max-w-2xl text-lg leading-relaxed text-white/70 sm:text-xl">
                      {p.desc}
                    </p>
                    <p className="mt-5 max-w-2xl text-base leading-relaxed text-brand-200 sm:text-lg">
                      {p.use}
                    </p>
                  </div>
                </Reveal>

                <Reveal
                  delay={0.1}
                  className={`relative ${flip ? "lg:order-1" : ""}`}
                >
                  <div className="absolute left-1/2 top-1/2 h-[28rem] w-[28rem] -translate-x-1/2 -translate-y-1/2 rounded-full bg-brand-500/25 blur-[120px]" />
                  <div className="relative">
                    <DeviceMockup type={p.type} label={p.name} />
                  </div>
                </Reveal>
              </div>
            );
          })}
        </div>
      </div>
    </section>
  );
};
